/* ═══════════════════════════════════════════════════════════════
   InterRAI Home Care — Questionario Strutturato
   Fonte: Allegato 4 - Questionario InterRAI Home Care (ASL Rieti)
   ═══════════════════════════════════════════════════════════════ */

export const interraiSteps = [
  /* ─── STEP 0: Stato Cognitivo & Comunicazione (Sez. C + D) ─── */
  {
    id: 'cognitivo',
    titolo: 'Sez. C/D — Stato Cognitivo e Comunicazione',
    sottotitolo: 'Capacita decisionale, memoria, comunicazione, udito e vista',
    icon: 'Brain',
    domande: [
      {
        id: 'C1',
        codice: 'C.1',
        label: 'Capacita di prendere le decisioni quotidiane',
        istruzione: 'Come la persona prende decisioni circa l\'organizzazione della giornata',
        opzioni: [
          { value: '0', label: 'Indipendente', desc: 'Decisioni sempre ragionevoli' },
          { value: '1', label: 'Semi-indipendente', desc: 'Qualche difficolta in situazioni nuove' },
          { value: '2', label: 'Compromissione minima', desc: 'Decisioni inadeguate in specifiche situazioni' },
          { value: '3', label: 'Compromissione moderata', desc: 'Decisioni inadeguate; richiesti suggerimenti in tutte le occasioni' },
          { value: '4', label: 'Compromissione severa', desc: 'Raramente/mai prende decisioni' },
          { value: '5', label: 'Non cosciente / Coma', desc: 'Passare alla Sezione G' },
        ],
      },
      {
        id: 'C2A',
        codice: 'C.2a',
        label: 'Memoria a breve termine',
        istruzione: 'Sembra ricordare dopo 5 minuti',
        opzioni: [
          { value: '0', label: 'Memoria OK', desc: 'Ricorda senza difficolta' },
          { value: '1', label: 'Problemi di memoria', desc: 'Non ricorda dopo 5 minuti' },
        ],
      },
      {
        id: 'C2B',
        codice: 'C.2b',
        label: 'Memoria procedurale',
        istruzione: 'Puo eseguire le attivita in ordine di sequenza senza suggerimenti',
        opzioni: [
          { value: '0', label: 'Memoria OK', desc: 'Esegue le sequenze correttamente' },
          { value: '1', label: 'Problemi di memoria', desc: 'Necessita suggerimenti per iniziare' },
        ],
      },
      {
        id: 'C2C',
        codice: 'C.2c',
        label: 'Memoria situazionale',
        istruzione: 'Riconosce nomi/volti dei caregiver e conosce la collocazione degli ambienti',
        opzioni: [
          { value: '0', label: 'Memoria OK', desc: 'Riconosce persone e luoghi' },
          { value: '1', label: 'Problemi di memoria', desc: 'Non riconosce persone o luoghi abituali' },
        ],
      },
      {
        id: 'C5',
        codice: 'C.5',
        label: 'Capacita decisionale vs 90 giorni precedenti',
        istruzione: 'Confronto con i 90 giorni precedenti la valutazione',
        opzioni: [
          { value: '0', label: 'Migliorata', desc: 'Capacita decisionale in miglioramento' },
          { value: '1', label: 'Invariata', desc: 'Nessun cambiamento significativo' },
          { value: '2', label: 'Ridotta', desc: 'Peggioramento della capacita decisionale' },
          { value: '8', label: 'Non valutabile', desc: 'Impossibile effettuare il confronto' },
        ],
      },
      {
        id: 'D1',
        codice: 'D.1',
        label: 'Capacita di farsi comprendere (Espressione)',
        istruzione: 'Capacita di esprimere informazioni in maniera verbale o non verbale',
        opzioni: [
          { value: '0', label: 'Compreso', desc: 'Esprime chiaramente cio che vuole comunicare' },
          { value: '1', label: 'In genere compreso', desc: 'Difficolta a trovare parole, ma con tempo si esprime' },
          { value: '2', label: 'Spesso compreso', desc: 'Difficolta, di solito richiesti suggerimenti' },
          { value: '3', label: 'Talvolta compreso', desc: 'Capacita limitata a richieste concrete' },
          { value: '4', label: 'Raramente/mai compreso', desc: 'Comunicazione gravemente compromessa' },
        ],
      },
      {
        id: 'D2',
        codice: 'D.2',
        label: 'Capacita di comprendere gli altri',
        istruzione: 'Comprensione del contenuto verbale, anche con apparecchio acustico',
        opzioni: [
          { value: '0', label: 'Comprende', desc: 'Comprende chiaramente' },
          { value: '1', label: 'In genere comprende', desc: 'Comprende la maggior parte della conversazione' },
          { value: '2', label: 'Spesso comprende', desc: 'Comprende se ripetuto o spiegato meglio' },
          { value: '3', label: 'Talvolta comprende', desc: 'Risponde solo a domande semplici e dirette' },
          { value: '4', label: 'Raramente comprende', desc: 'Comprensione gravemente compromessa' },
        ],
      },
      {
        id: 'D4',
        codice: 'D.4',
        label: 'Vista',
        istruzione: 'Capacita di vedere in condizioni di illuminazione adeguata, con occhiali se usati',
        opzioni: [
          { value: '0', label: 'Adeguata', desc: 'Vede dettagli minuti e caratteri di stampa' },
          { value: '1', label: 'Minima difficolta', desc: 'Vede caratteri grandi ma non quelli piccoli' },
          { value: '2', label: 'Difficolta moderata', desc: 'Vista limitata, riconosce gli oggetti' },
          { value: '3', label: 'Difficolta grave', desc: 'Riconoscimento dubbio degli oggetti' },
          { value: '4', label: 'Cecita completa', desc: 'Nessuna vista, vede solo luce/colori/contorni' },
        ],
      },
    ],
  },

  /* ─── STEP 1: Umore e Comportamento (Sez. E) ─── */
  {
    id: 'umore',
    titolo: 'Sez. E — Umore e Comportamento',
    sottotitolo: 'Indicatori di depressione, ansia, umore triste e sintomi comportamentali',
    icon: 'Heart',
    domande: [
      {
        id: 'E1A',
        codice: 'E.1a',
        label: 'Affermazioni negative',
        istruzione: '"Non vale la pena di vivere, non mi importa piu nulla, lasciatemi morire"',
        opzioni: [
          { value: '0', label: 'Non presente', desc: 'Indicatore non presente' },
          { value: '1', label: 'Presente, non recente', desc: 'Presente, ma non negli ultimi 3 giorni' },
          { value: '2', label: '1-2 degli ultimi 3gg', desc: 'Manifestatosi in 1-2 degli ultimi 3 giorni' },
          { value: '3', label: 'Ogni giorno', desc: 'Manifestatosi in ognuno degli ultimi 3 giorni' },
        ],
      },
      {
        id: 'E1B',
        codice: 'E.1b',
        label: 'Persistente rabbia contro se o altri',
        istruzione: 'Facilmente annoiato, si arrabbia quando riceve assistenza',
        opzioni: [
          { value: '0', label: 'Non presente', desc: 'Indicatore non presente' },
          { value: '1', label: 'Presente, non recente', desc: 'Presente, ma non negli ultimi 3 giorni' },
          { value: '2', label: '1-2 degli ultimi 3gg', desc: 'Manifestatosi in 1-2 degli ultimi 3 giorni' },
          { value: '3', label: 'Ogni giorno', desc: 'Manifestatosi in ognuno degli ultimi 3 giorni' },
        ],
      },
      {
        id: 'E1C',
        codice: 'E.1c',
        label: 'Espressioni di paure irreali',
        istruzione: 'Paura di essere abbandonato, di stare solo, paura intensa di oggetti o situazioni',
        opzioni: [
          { value: '0', label: 'Non presente', desc: 'Indicatore non presente' },
          { value: '1', label: 'Presente, non recente', desc: 'Presente, ma non negli ultimi 3 giorni' },
          { value: '2', label: '1-2 degli ultimi 3gg', desc: 'Manifestatosi in 1-2 degli ultimi 3 giorni' },
          { value: '3', label: 'Ogni giorno', desc: 'Manifestatosi in ognuno degli ultimi 3 giorni' },
        ],
      },
      {
        id: 'E1F',
        codice: 'E.1f',
        label: 'Espressione del volto triste, dolente, corrucciata',
        istruzione: 'Sguardo accigliato, espressione di sofferenza',
        opzioni: [
          { value: '0', label: 'Non presente', desc: 'Indicatore non presente' },
          { value: '1', label: 'Presente, non recente', desc: 'Presente, ma non negli ultimi 3 giorni' },
          { value: '2', label: '1-2 degli ultimi 3gg', desc: 'Manifestatosi in 1-2 degli ultimi 3 giorni' },
          { value: '3', label: 'Ogni giorno', desc: 'Manifestatosi in ognuno degli ultimi 3 giorni' },
        ],
      },
      {
        id: 'E1G',
        codice: 'E.1g',
        label: 'Pianto ricorrente',
        istruzione: 'Episodi ripetuti di pianto osservati negli ultimi 3 giorni',
        opzioni: [
          { value: '0', label: 'Non presente', desc: 'Indicatore non presente' },
          { value: '1', label: 'Presente, non recente', desc: 'Presente, ma non negli ultimi 3 giorni' },
          { value: '2', label: '1-2 degli ultimi 3gg', desc: 'Manifestatosi in 1-2 degli ultimi 3 giorni' },
          { value: '3', label: 'Ogni giorno', desc: 'Manifestatosi in ognuno degli ultimi 3 giorni' },
        ],
      },
      {
        id: 'E1I',
        codice: 'E.1i',
        label: 'Perdita di interesse per attivita piacevoli',
        istruzione: 'Ha perso interesse per attivita che prima gli piacevano, incluso stare con amici e parenti',
        opzioni: [
          { value: '0', label: 'Non presente', desc: 'Indicatore non presente' },
          { value: '1', label: 'Presente, non recente', desc: 'Presente, ma non negli ultimi 3 giorni' },
          { value: '2', label: '1-2 degli ultimi 3gg', desc: 'Manifestatosi in 1-2 degli ultimi 3 giorni' },
          { value: '3', label: 'Ogni giorno', desc: 'Manifestatosi in ognuno degli ultimi 3 giorni' },
        ],
      },
      {
        id: 'E3A',
        codice: 'E.3a',
        label: 'Vagabondaggio',
        istruzione: 'Senza alcun motivo razionale, senza badare ai propri bisogni e alla sicurezza',
        opzioni: [
          { value: '0', label: 'Non presente', desc: 'Comportamento non presente' },
          { value: '1', label: 'Presente, non recente', desc: 'Presente, ma non negli ultimi 3 giorni' },
          { value: '2', label: '1-2 degli ultimi 3gg', desc: 'Manifestatosi in 1-2 degli ultimi 3 giorni' },
          { value: '3', label: 'Ogni giorno', desc: 'Manifestatosi in ognuno degli ultimi 3 giorni' },
        ],
      },
      {
        id: 'E3B',
        codice: 'E.3b',
        label: 'Aggressivita verbale',
        istruzione: 'Ha spaventato, insultato, minacciato, gridato ad altre persone',
        opzioni: [
          { value: '0', label: 'Non presente', desc: 'Comportamento non presente' },
          { value: '1', label: 'Presente, non recente', desc: 'Presente, ma non negli ultimi 3 giorni' },
          { value: '2', label: '1-2 degli ultimi 3gg', desc: 'Manifestatosi in 1-2 degli ultimi 3 giorni' },
          { value: '3', label: 'Ogni giorno', desc: 'Manifestatosi in ognuno degli ultimi 3 giorni' },
        ],
      },
    ],
  },

  /* ─── STEP 2: Funzione Fisica — ADL (Sez. G) ─── */
  {
    id: 'funzione_fisica',
    titolo: 'Sez. G — Funzione Fisica (ADL)',
    sottotitolo: 'Autonomia nelle attivita della vita quotidiana negli ultimi 3 giorni',
    icon: 'Activity',
    domande: [
      {
        id: 'G2A',
        codice: 'G.2a',
        label: 'Uso della vasca / doccia',
        istruzione: 'Come esegue il trasferimento e come viene lavata ogni parte del corpo',
        opzioni: [
          { value: '0', label: 'Indipendente', desc: 'Nessuna necessita di aiuto o supervisione' },
          { value: '1', label: 'Organizzazione', desc: 'Mezzo o dispositivo fornito/messo a portata di mano' },
          { value: '2', label: 'Supervisione', desc: 'Controllo, incoraggiamento, suggerimento' },
          { value: '3', label: 'Assistenza limitata', desc: 'Aiuto fisico guidato, senza sostenere il peso' },
          { value: '4', label: 'Assistenza intensiva', desc: 'Aiuto nel sostenere il peso, ma fa almeno il 50%' },
          { value: '5', label: 'Massima assistenza', desc: 'Aiuto di 2+ persone o fa meno del 50%' },
          { value: '6', label: 'Dipendenza totale', desc: 'Attivita svolta completamente da altri' },
        ],
      },
      {
        id: 'G2B',
        codice: 'G.2b',
        label: 'Igiene personale',
        istruzione: 'Pettinarsi, lavarsi i denti, farsi la barba, truccarsi, lavarsi la faccia e le mani',
        opzioni: [
          { value: '0', label: 'Indipendente', desc: 'Nessuna necessita di aiuto o supervisione' },
          { value: '1', label: 'Organizzazione', desc: 'Mezzo o dispositivo fornito/messo a portata di mano' },
          { value: '2', label: 'Supervisione', desc: 'Controllo, incoraggiamento, suggerimento' },
          { value: '3', label: 'Assistenza limitata', desc: 'Aiuto fisico guidato, senza sostenere il peso' },
          { value: '4', label: 'Assistenza intensiva', desc: 'Aiuto nel sostenere il peso, fa almeno il 50%' },
          { value: '5', label: 'Massima assistenza', desc: 'Aiuto di 2+ persone o fa meno del 50%' },
          { value: '6', label: 'Dipendenza totale', desc: 'Attivita svolta completamente da altri' },
        ],
      },
      {
        id: 'G2E',
        codice: 'G.2e',
        label: 'Camminare',
        istruzione: 'Come cammina tra diversi punti dell\'abitazione sullo stesso livello',
        opzioni: [
          { value: '0', label: 'Indipendente', desc: 'Nessuna necessita di aiuto o supervisione' },
          { value: '1', label: 'Organizzazione', desc: 'Mezzo/dispositivo fornito' },
          { value: '2', label: 'Supervisione', desc: 'Controllo, incoraggiamento, suggerimento' },
          { value: '3', label: 'Assistenza limitata', desc: 'Aiuto fisico guidato, senza sostenere il peso' },
          { value: '4', label: 'Assistenza intensiva', desc: 'Aiuto nel sostenere il peso, fa almeno il 50%' },
          { value: '5', label: 'Massima assistenza', desc: 'Aiuto di 2+ persone o fa meno del 50%' },
          { value: '6', label: 'Dipendenza totale', desc: 'Attivita svolta completamente da altri' },
          { value: '8', label: 'Attivita mai eseguita', desc: 'Non eseguita durante l\'intero periodo' },
        ],
      },
      {
        id: 'G2G',
        codice: 'G.2g',
        label: 'Trasferimento sul W.C.',
        istruzione: 'Come esegue il trasferimento sul W.C. o sulla comoda',
        opzioni: [
          { value: '0', label: 'Indipendente', desc: 'Nessuna necessita di aiuto o supervisione' },
          { value: '1', label: 'Organizzazione', desc: 'Mezzo/dispositivo fornito' },
          { value: '2', label: 'Supervisione', desc: 'Controllo, incoraggiamento, suggerimento' },
          { value: '3', label: 'Assistenza limitata', desc: 'Aiuto fisico guidato, senza sostenere il peso' },
          { value: '4', label: 'Assistenza intensiva', desc: 'Aiuto nel sostenere il peso, fa almeno il 50%' },
          { value: '5', label: 'Massima assistenza', desc: 'Aiuto di 2+ persone o fa meno del 50%' },
          { value: '6', label: 'Dipendenza totale', desc: 'Attivita svolta completamente da altri' },
        ],
      },
      {
        id: 'G2I',
        codice: 'G.2i',
        label: 'Mobilita a letto',
        istruzione: 'Come si sdraia e si alza, si gira da un lato all\'altro, assume posizioni nel letto',
        opzioni: [
          { value: '0', label: 'Indipendente', desc: 'Nessuna necessita di aiuto o supervisione' },
          { value: '1', label: 'Organizzazione', desc: 'Mezzo/dispositivo fornito' },
          { value: '2', label: 'Supervisione', desc: 'Controllo, incoraggiamento, suggerimento' },
          { value: '3', label: 'Assistenza limitata', desc: 'Aiuto fisico guidato, senza sostenere il peso' },
          { value: '4', label: 'Assistenza intensiva', desc: 'Aiuto nel sostenere il peso, fa almeno il 50%' },
          { value: '5', label: 'Massima assistenza', desc: 'Aiuto di 2+ persone o fa meno del 50%' },
          { value: '6', label: 'Dipendenza totale', desc: 'Attivita svolta completamente da altri' },
        ],
      },
      {
        id: 'G2J',
        codice: 'G.2j',
        label: 'Mangiare',
        istruzione: 'Come si alimenta, inclusa nutrizione con sondino o parenterale',
        opzioni: [
          { value: '0', label: 'Indipendente', desc: 'Nessuna necessita di aiuto o supervisione' },
          { value: '1', label: 'Organizzazione', desc: 'Mezzo/dispositivo fornito' },
          { value: '2', label: 'Supervisione', desc: 'Controllo, incoraggiamento, suggerimento' },
          { value: '3', label: 'Assistenza limitata', desc: 'Aiuto fisico guidato, senza sostenere il peso' },
          { value: '4', label: 'Assistenza intensiva', desc: 'Aiuto nel sostenere il peso, fa almeno il 50%' },
          { value: '5', label: 'Massima assistenza', desc: 'Aiuto di 2+ persone o fa meno del 50%' },
          { value: '6', label: 'Dipendenza totale', desc: 'Attivita svolta completamente da altri' },
        ],
      },
    ],
  },

  /* ─── STEP 3: Condizioni Cliniche (Sez. H + I + J) ─── */
  {
    id: 'condizioni',
    titolo: 'Sez. H/I/J — Condizioni Cliniche',
    sottotitolo: 'Continenza, diagnosi, cadute, dolore e instabilita',
    icon: 'Pill',
    domande: [
      {
        id: 'H1',
        codice: 'H.1',
        label: 'Continenza urinaria',
        istruzione: 'Controllo della funzione urinaria',
        opzioni: [
          { value: '0', label: 'Continente', desc: 'Controllo completo, nessun catetere' },
          { value: '1', label: 'Continente con catetere', desc: 'Controllo tramite catetere o stomia' },
          { value: '2', label: 'Raramente incontinente', desc: 'Episodi, ma non negli ultimi 3 giorni' },
          { value: '3', label: 'Occasionalmente', desc: 'Incontinente non quotidianamente' },
          { value: '4', label: 'Frequentemente', desc: 'Quotidianamente, con episodi di controllo' },
          { value: '5', label: 'Incontinente', desc: 'Nessun tipo di controllo' },
        ],
      },
      {
        id: 'J1',
        codice: 'J.1',
        label: 'Cadute',
        istruzione: 'Episodi di caduta negli ultimi 90 giorni',
        opzioni: [
          { value: '0', label: 'Nessuna caduta', desc: 'Nessuna caduta negli ultimi 90 giorni' },
          { value: '1', label: 'Caduta 31-90gg fa', desc: 'Nessuna negli ultimi 30gg, ma tra 31 e 90gg fa' },
          { value: '2', label: '1 caduta (ultimi 30gg)', desc: 'Una caduta negli ultimi 30 giorni' },
          { value: '3', label: '2+ cadute (ultimi 30gg)', desc: 'Due o piu cadute negli ultimi 30 giorni' },
        ],
      },
      {
        id: 'J6A',
        codice: 'J.6a',
        label: 'Frequenza del dolore',
        istruzione: 'Frequenza con cui la persona lamenta o mostra segni di dolore',
        opzioni: [
          { value: '0', label: 'Non presente', desc: 'Nessun dolore riferito o osservato' },
          { value: '1', label: 'Presente, non recente', desc: 'Presente, ma non negli ultimi 3 giorni' },
          { value: '2', label: '1-2 degli ultimi 3gg', desc: 'Dolore manifestatosi in 1-2 degli ultimi 3 giorni' },
          { value: '3', label: 'Quotidiano', desc: 'Dolore manifestatosi quotidianamente' },
        ],
      },
      {
        id: 'J6B',
        codice: 'J.6b',
        label: 'Livello piu alto di dolore',
        istruzione: 'Intensita massima del dolore riferito o osservato',
        opzioni: [
          { value: '0', label: 'Nessun dolore', desc: 'Assenza completa di dolore' },
          { value: '1', label: 'Intensita lieve', desc: 'Dolore lieve, tollerabile' },
          { value: '2', label: 'Intensita moderata', desc: 'Dolore moderato, fastidioso' },
          { value: '3', label: 'Intensita severa', desc: 'Dolore severo, limitante' },
          { value: '4', label: 'Terribile/insopportabile', desc: 'A volte il dolore e insopportabile' },
        ],
      },
      {
        id: 'J4',
        codice: 'J.4',
        label: 'Dispnea',
        istruzione: 'Presenza e gravita della dispnea (difficolta respiratoria)',
        opzioni: [
          { value: '0', label: 'Sintomo assente', desc: 'Nessuna difficolta respiratoria' },
          { value: '1', label: 'Presente, non recente', desc: 'Assente a riposo, presente per attivita moderata' },
          { value: '2', label: 'Con attivita quotidiane', desc: 'Assente a riposo, presente con le normali ADL' },
          { value: '3', label: 'Presente a riposo', desc: 'Dispnea anche a riposo' },
        ],
      },
      {
        id: 'J5',
        codice: 'J.5',
        label: 'Astenia',
        istruzione: 'Livello di astenia (ridotta energia fisica)',
        opzioni: [
          { value: '0', label: 'Non presente', desc: 'Nessuna astenia' },
          { value: '1', label: 'Minima', desc: 'Ridotta energia, ma svolge normali attivita' },
          { value: '2', label: 'Moderata', desc: 'Non in grado di completare le normali attivita' },
          { value: '3', label: 'Severa', desc: 'Non in grado di iniziare alcune attivita' },
          { value: '4', label: 'Totale', desc: 'Non in grado di iniziare alcuna attivita quotidiana' },
        ],
      },
      {
        id: 'J7A',
        codice: 'J.7a',
        label: 'Instabilita delle condizioni cliniche',
        istruzione: 'Le condizioni cliniche causano instabilita',
        opzioni: [
          { value: '0', label: 'No', desc: 'Condizioni cliniche stabili' },
          { value: '1', label: 'Si', desc: 'Condizioni cliniche instabili' },
        ],
      },
      {
        id: 'L1',
        codice: 'L.1',
        label: 'Ulcera da pressione (stadio piu grave)',
        istruzione: 'Stadio piu grave di ulcera da pressione presente',
        opzioni: [
          { value: '0', label: 'Nessuna', desc: 'Nessuna ulcera da pressione' },
          { value: '1', label: 'Stadio I', desc: 'Area di arrossamento cutaneo persistente' },
          { value: '2', label: 'Stadio II', desc: 'Parziale perdita di strati di cute' },
          { value: '3', label: 'Stadio III', desc: 'Presenza di profondo cratere cutaneo' },
          { value: '4', label: 'Stadio IV', desc: 'Perdita a tutto spessore con esposizione muscolo/osso' },
          { value: '5', label: 'Non stadiabile', desc: 'Predominante escara necrotica' },
        ],
      },
    ],
  },
];

/* Step labels and icons for the stepper */
export const wizardStepsMeta = [
  { label: 'Cognitivo', icon: 'Brain' },
  { label: 'Umore', icon: 'Heart' },
  { label: 'ADL', icon: 'Activity' },
  { label: 'Clinica', icon: 'Pill' },
  { label: 'PAI & Firma', icon: 'FileSignature' },
];
